import { type MouseEvent, useMemo, type VoidFunctionComponent } from 'react';

import { RevisionLicenseConclusionScope } from '../../../../shared/model-typings/revisionlicenseconclusion';
import useGetRevisionConclusions from '../../../hooks/api/correctionsV2/useGetRevisionConclusions';
import useIsLicenseConcludedEnabled from '../../../hooks/features/useIsLicenseConcludedEnabled';
import Button from '../../generic/Button/Button';
import useConcludeLicense, {
  type ChooseParams,
} from '../dependenciesV2/DependencyDrawer/content/DependencyLicenses/hooks/useConcludeLicense';
import useUnconcludeLicense from '../dependenciesV2/DependencyDrawer/content/DependencyLicenses/hooks/useUnconcludeLicense';

type Props = {
  licenseId: string;
  revisionLocator: string;
  projectLocator: string;
  organizationId: number;
};

export const ConcludeLicenseButton: VoidFunctionComponent<Props> = ({
  licenseId,
  revisionLocator,
  projectLocator,
  organizationId,
}) => {
  const isLicenseConcludedEnabled = useIsLicenseConcludedEnabled(organizationId);
  const { data: conclusions } = useGetRevisionConclusions(revisionLocator);

  const concludedLicenses = conclusions?.scoped.licenses ?? [];
  const isConcluded = concludedLicenses.includes(licenseId);

  const chooseParams: ChooseParams = useMemo(() => ({
    dependencyRevisionLocator: revisionLocator,
    scope: {
      scope: RevisionLicenseConclusionScope.REVISION,
      projectLocator,
      revisionLocator,
    },
    originId: revisionLocator,
  }), [revisionLocator, projectLocator]);

  const { mutateAsync: concludeLicense, isPending: isConcluding } = useConcludeLicense(chooseParams);
  const { mutateAsync: unconcludeLicense, isPending: isUnconcluding } = useUnconcludeLicense(chooseParams);

  const isPending = isConcluding || isUnconcluding;

  if (!isLicenseConcludedEnabled) {
    return null;
  }

  const onConclude = async (e: MouseEvent) => {
    e.stopPropagation();
    if (isConcluded) {
      await unconcludeLicense({ chosenLicense: licenseId, chooseParams });
    } else {
      await concludeLicense({ chosenLicense: licenseId, chooseParams });
    }
  };

  const buttonType = isConcluded ? 'error' : 'outline';
  const buttonLabel = isConcluded ? 'Unconclude' : 'Conclude';

  return (
    <Button
      type={buttonType}
      size="micro"
      onClick={onConclude}
      isDisabled={isPending}
    >
      {isPending ? 'Loading...' : buttonLabel}
    </Button>
  );
};
